import router from '@/router';
import { RouterV2Enum } from '@/router/router-names';
import useDispatchProcess from '@/hooks/useDispatchProcess';
import { ubtSendError } from '@/shared/utils/ubt';
import { ubtSetupPageButton } from '@/shared/utils/ubt/ubt-setup';
import { jumpToNewVersion } from '@/shared/utils/jumpTo';
import type { CustomClickConfig } from '../types';
import { CustomClickEventNameEnum } from '../enums';

/**
 * 活动页面点击事件处理 hooks 返回值类型
 */
export interface UseActivityClickReturn {
  /** 点击事件处理函数 */
  handleClick: () => void;
}

/**
 * 活动页面点击事件处理 hooks
 * @param customClick 点击事件配置
 * @returns 点击处理函数
 */
export function useActivityClick(
  customClick?: CustomClickConfig,
  ubtCommonParams: Dictionary = {},
): UseActivityClickReturn {
  // 复用流程分发 hooks
  const { creditProcessDispatch, applyProcessDispatch } = useDispatchProcess();

  /**
   * 处理点击事件
   * 包含埋点上报和路由跳转逻辑
   */
  const handleClick = () => {
    if (!customClick) return;

    const { eventName, ubtModuleId, jumpToRouteConfig } = customClick;
    const { routePath, isFinanceLoanH5 } = jumpToRouteConfig || {};

    // 埋点上报
    if (ubtModuleId) {
      ubtSetupPageButton(ubtModuleId, ubtCommonParams);
    }

    /**
     * 执行eventName对应的事件处理
     * JUMP_TO_ROUTE，可自定义路由跳转
     */
    switch (eventName) {
      case CustomClickEventNameEnum.JUMP_TO_CREDIT:
        // 跳转到授信页面（复用授信流程分发，包含前置检查）
        creditProcessDispatch();
        break;
      case CustomClickEventNameEnum.JUMP_TO_APPLY:
        // 跳转到申请页面（复用提现流程分发，包含前置检查）
        applyProcessDispatch();
        break;
      case CustomClickEventNameEnum.JUMP_TO_MINE:
        // 跳转到我的页面
        router.push({
          path: RouterV2Enum.myProfile,
        });
        break;
      case CustomClickEventNameEnum.JUMP_TO_ROUTE:
        // 跳转到自定义路由页面
        if (isFinanceLoanH5) {
          jumpToNewVersion(routePath || '', false);
        } else {
          router.push({ path: routePath || '' });
        }
        break;
      default:
        ubtSendError({
          name: 'unknown_activity_home_click_event',
          message: eventName,
        });
    }
  };

  return {
    handleClick,
  };
}
